/* iter-51 — Author ISBN Manager dialog.
 *
 * Authors request ISBNs for their finished manuscripts and fill in the
 * BISG-aligned metadata (title/subtitle, contributors, format, edition,
 * language, trim size, page count, pub date, BISAC, retail price). The
 * actual ISBN string is assigned by an Administrator from the bookstore
 * admin panel once the manuscript clears review — the author can see
 * the status here ("Requested" → "Issued") and view the assigned ISBN.
 *
 * Publisher of Record is hard-coded "Crown & Rose LLC" and surfaced
 * as a read-only field; the backend ignores any attempt to override it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Plus, RefreshCw, BookOpen, Save, Hash, Lock, Wand2, FileDown } from 'lucide-react';
import { isbnApi } from '@/lib/api';
import { usePW } from '@/lib/store';
import { formatIsbn, splitCsv as splitContributors, intOrNull, dollarsToCents } from '@/lib/format';
import BowkerWizard from './BowkerWizard';   // Feb-2026 author-facing Bowker wizard
import PublishEverywhereButton from '@/components/PublishEverywhereButton';   // Feb-2026 cross-post

const PUBLISHER = 'Crown & Rose LLC';
const FORMATS = ['paperback', 'hardcover', 'audiobook', 'ebook'];

const STATUS_STYLES = {
  pending:      { label: 'Pending',      cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  requested:    { label: 'Requested',    cls: 'bg-sky-100 text-sky-900 border-sky-300' },
  issued:       { label: 'Issued',       cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  discontinued: { label: 'Discontinued', cls: 'bg-rose-100 text-rose-900 border-rose-300' },
};

export default function IsbnManagerDialog({ open, onOpenChange }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [wizardMode, setWizardMode] = useState(false);   // Feb-2026 Bowker wizard toggle

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const d = await isbnApi.listMine();
      setRows(d.records || []);
      // Pre-select the first row only on the very first load (when there's
      // no current selection). Keep activeId out of deps so selecting a
      // different row doesn't trigger a full list re-fetch.
      setActiveId((cur) => cur || d.records?.[0]?.id || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load ISBN records.');
      setRows([]);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const active = (rows || []).find((r) => r.id === activeId) || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" data-testid="isbn-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-primary" /> ISBN Manager
          </DialogTitle>
          <DialogDescription>
            Request an ISBN for your manuscript and fill in its bibliographic
            metadata. An administrator assigns the ISBN string once your book
            is approved. Publisher of Record is{' '}
            <strong>{PUBLISHER}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-4">
          {/* Left rail — request list */}
          <aside className="col-span-4 flex flex-col gap-2 border-r border-border pr-3">
            <div className="flex items-center justify-between">
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
                My ISBN requests
              </span>
              <div className="flex items-center gap-1">
                <IconBtn onClick={load} disabled={busy} title="Refresh" data-testid="isbn-refresh">
                  <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
                </IconBtn>
                <NewIsbnButton onCreated={(rid) => { setActiveId(rid); load(); }} />
              </div>
            </div>
            <div className="max-h-[480px] overflow-y-auto flex flex-col gap-1.5" data-testid="isbn-list">
              {rows === null ? (
                <LoaderRow text="Loading your ISBN requests…" />
              ) : rows.length === 0 ? (
                <EmptyState />
              ) : (
                rows.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setActiveId(r.id)}
                    data-testid={`isbn-row-${r.id}`}
                    className={`group flex flex-col items-start gap-1 rounded-sm border px-2.5 py-2 text-left transition hover:bg-secondary ${
                      activeId === r.id ? 'border-primary bg-primary/5' : 'border-border bg-card'
                    }`}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="line-clamp-1 text-[12px] font-semibold">{r.title || 'Untitled'}</span>
                      <StatusPill status={r.status} />
                    </div>
                    <div className="text-[10.5px] text-muted-foreground">
                      {r.isbn ? `ISBN ${formatIsbn(r.isbn)}` : 'ISBN not yet assigned'}
                    </div>
                  </button>
                ))
              )}
            </div>
          </aside>

          {/* Right pane — editor OR wizard */}
          <section className="col-span-8 min-h-[480px]">
            {!active ? (
              <div className="flex h-full items-center justify-center text-center text-[12px] text-muted-foreground">
                {rows && rows.length === 0
                  ? 'Click "New request" to get started.'
                  : 'Select a record on the left to view or edit it.'}
              </div>
            ) : wizardMode ? (
              <BowkerWizard record={active} onSaved={load} onExit={() => setWizardMode(false)} />
            ) : (
              <IsbnEditor record={active} onSaved={load} onEnterWizard={() => setWizardMode(true)} />
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────── widgets

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span data-testid={`isbn-status-${status}`}
          className={`shrink-0 rounded-full border px-2 py-[1.5px] text-[10px] font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
}

function IconBtn({ children, ...p }) {
  return (
    <button type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded-sm border border-border bg-background hover:bg-secondary disabled:opacity-60"
            {...p}>{children}</button>
  );
}

function LoaderRow({ text }) {
  return (
    <div className="flex items-center gap-2 px-2 py-3 text-[12px] text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> {text}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-md border border-dashed border-border bg-card/40 p-4 text-center text-[11.5px] text-muted-foreground">
      <BookOpen className="mx-auto mb-1 h-4 w-4" />
      No ISBN requests yet. Click <strong>New request</strong> to start one.
    </div>
  );
}

function NewIsbnButton({ onCreated }) {
  const { project } = usePW();
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const seed = {
        title: project?.title || 'Untitled manuscript',
        language: 'English',
        format: 'paperback',
      };
      if (project?.id) seed.project_id = project.id;
      const d = await isbnApi.request(seed);
      toast.success('ISBN request created.');
      onCreated?.(d.record.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the request.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={create}
      disabled={busy}
      data-testid="isbn-new"
      className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} New request
    </button>
  );
}

// ─────────────────────────────────────────────────────────────── editor

function IsbnEditor({ record, onSaved, onEnterWizard }) {
  const [draft, setDraft] = useState(() => ({ ...record }));
  const [busy, setBusy] = useState(false);
  const issued = record.status === 'issued' || record.status === 'discontinued';

  // Keep the form in sync when the selected record changes.
  // Intentionally only re-syncs when the record identity changes — including the
  // whole `record` object would clobber in-progress edits on every parent re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDraft({ ...record }); }, [record.id]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        title:              draft.title,
        subtitle:           draft.subtitle || null,
        contributors:       splitContributors(draft._contributors_text ?? (draft.contributors || []).join(', ')),
        format:             draft.format || null,
        edition:            draft.edition || null,
        language:           draft.language || null,
        trim_size:          draft.trim_size || null,
        page_count:         intOrNull(draft.page_count),
        publication_date:   draft.publication_date || null,
        bisac_codes:        splitContributors(draft._bisac_text ?? (draft.bisac_codes || []).join(', ')),
        retail_price_cents: dollarsToCents(draft._retail_dollars ?? (record.retail_price_cents == null ? '' : (record.retail_price_cents / 100).toFixed(2))),
        currency:           (draft.currency || 'USD').toUpperCase(),
        project_id:         draft.project_id || null,
        bookstore_book_id:  draft.bookstore_book_id || null,
      };
      await isbnApi.updateMine(record.id, patch);
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-3" data-testid={`isbn-editor-${record.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StatusPill status={record.status} />
          {record.isbn && (
            <span className="rounded-sm border border-border bg-secondary px-2 py-[1.5px] text-[11px] font-mono">
              ISBN {formatIsbn(record.isbn)}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={busy || issued}
          data-testid="isbn-save"
          className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save changes
        </button>
        {!issued && onEnterWizard && (
          <button
            type="button"
            onClick={onEnterWizard}
            data-testid="isbn-launch-wizard"
            className="inline-flex items-center gap-1.5 rounded-sm border border-primary bg-primary/5 px-3 py-1.5 text-[11.5px] font-semibold text-primary hover:bg-primary/10"
          >
            <Wand2 className="h-3 w-3" /> Bowker wizard
          </button>
        )}
        {/* Feb-2026 · Author-facing single-record ONIX 3.0 download. */}
        <a
          href={isbnApi.onixUrl(record.id)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="isbn-onix-download"
          title="Download ONIX 3.0 XML for Bowker registration"
          className={`inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-3 py-1.5 text-[11.5px] font-semibold hover:bg-secondary ${!record.title ? 'pointer-events-none opacity-50' : ''}`}
        >
          <FileDown className="h-3 w-3" /> ONIX 3.0
        </a>
        {/* Feb-2026 · Cross-post the cover reveal / launch teaser via Zernio. */}
        <PublishEverywhereButton
          size="sm"
          variant="outline"
          label="Cross-post"
          testid="isbn-cross-post"
          title={record.title || ''}
          synopsis={record.subtitle || ''}
          initialMedia={record.cover_data_url
            ? { url: record.cover_data_url, type: 'image' }
            : null}
          bookId={record.bookstore_book_id || null}
          destUrl={record.bookstore_book_id
            ? `${window.location.origin}/bookstore/book/${record.bookstore_book_id}`
            : null}
        />
      </div>

      {issued && (
        <div className="flex items-center gap-2 rounded-sm border border-emerald-300/50 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] text-emerald-900">
          <Lock className="h-3 w-3" />
          Issued records are locked to authors. Contact an administrator to amend metadata.
        </div>
      )}

      <div className="grid grid-cols-12 gap-3 overflow-y-auto pr-1">
        <Field label="Title" required className="col-span-8"
               value={draft.title || ''} onChange={(v) => set('title', v)}
               testid="isbn-field-title" disabled={issued} />
        <Field label="Subtitle" className="col-span-4"
               value={draft.subtitle || ''} onChange={(v) => set('subtitle', v)}
               testid="isbn-field-subtitle" disabled={issued} />

        <Field label="Contributor(s) (comma-separated)" className="col-span-8"
               value={draft._contributors_text ?? (draft.contributors || []).join(', ')}
               onChange={(v) => set('_contributors_text', v)}
               testid="isbn-field-contributors" disabled={issued} />
        <ReadOnlyField label="Publisher of Record" value={PUBLISHER}
                        className="col-span-4" testid="isbn-field-publisher" />

        <SelectField label="Format & Binding" className="col-span-4"
                       value={draft.format || ''}
                       onChange={(v) => set('format', v)}
                       options={FORMATS} testid="isbn-field-format" disabled={issued} />
        <Field label="Edition" className="col-span-4"
               value={draft.edition || ''} onChange={(v) => set('edition', v)}
               placeholder="First Edition" testid="isbn-field-edition" disabled={issued} />
        <Field label="Language" className="col-span-4"
               value={draft.language || ''} onChange={(v) => set('language', v)}
               placeholder="English" testid="isbn-field-language" disabled={issued} />

        <Field label="Trim size" className="col-span-4"
               value={draft.trim_size || ''} onChange={(v) => set('trim_size', v)}
               placeholder='6" × 9"' testid="isbn-field-trim" disabled={issued} />
        <Field label="Page count" className="col-span-4"
               value={draft.page_count == null ? '' : String(draft.page_count)}
               onChange={(v) => set('page_count', v)}
               type="number" placeholder="312" testid="isbn-field-pages" disabled={issued} />
        <Field label="Publication date" className="col-span-4"
               value={draft.publication_date || ''} onChange={(v) => set('publication_date', v)}
               type="date" testid="isbn-field-pubdate" disabled={issued} />

        <Field label="BISAC subject codes (comma-separated)" className="col-span-8"
               value={draft._bisac_text ?? (draft.bisac_codes || []).join(', ')}
               onChange={(v) => set('_bisac_text', v)}
               placeholder="FIC014000, FIC031000" testid="isbn-field-bisac" disabled={issued} />
        <Field label="Retail price (USD)" className="col-span-2"
               value={draft._retail_dollars ?? (record.retail_price_cents == null ? '' : (record.retail_price_cents / 100).toFixed(2))}
               onChange={(v) => set('_retail_dollars', v)}
               type="number" placeholder="17.99" testid="isbn-field-price" disabled={issued} />
        <Field label="Currency" className="col-span-2"
               value={draft.currency || 'USD'} onChange={(v) => set('currency', v)}
               placeholder="USD" testid="isbn-field-currency" disabled={issued} />

        <Field label="Linked The Loom project id" className="col-span-6"
               value={draft.project_id || ''} onChange={(v) => set('project_id', v)}
               placeholder="project_xxx" testid="isbn-field-project" disabled={issued} />
        <Field label="Linked Bookstore book id" className="col-span-6"
               value={draft.bookstore_book_id || ''}
               onChange={(v) => set('bookstore_book_id', v)}
               placeholder="book_xxx" testid="isbn-field-book" disabled={issued} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── form helpers

function Field({ label, value, onChange, testid, placeholder = '',
                  className = '', type = 'text', required = false, disabled = false }) {
  return (
    <label className={`flex flex-col gap-0.5 ${className}`}>
      <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
        {label}{required ? ' *' : ''}
      </span>
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={testid}
        className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary disabled:bg-muted disabled:text-muted-foreground"
      />
    </label>
  );
}

function ReadOnlyField({ label, value, className = '', testid }) {
  return (
    <label className={`flex flex-col gap-0.5 ${className}`}>
      <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <div data-testid={testid}
            className="h-8 truncate rounded-sm border border-border bg-muted px-2 py-1 text-[12px] text-muted-foreground">
        {value}
      </div>
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid, className = '', disabled = false }) {
  return (
    <label className={`flex flex-col gap-0.5 ${className}`}>
      <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <select
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={testid}
        className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary disabled:bg-muted disabled:text-muted-foreground"
      >
        <option value="">—</option>
        {options.map((o) => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
      </select>
    </label>
  );
}
